import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
    X,
    Plus,
    Calendar,
    User,
    Clock,
    CheckCircle,
    Phone,
    MessageSquare,
    Target,
    Activity,
} from "lucide-react";
import { useSidebarContext } from "../scenes/global/sidebar/SidebarContext";
import { createLeadFollowUp, getLeadFollowUps } from "../api";
import { useSnackbar } from "notistack";

const LEAD_STATUSES = [
    { value: "", label: "— No change —" },
    { value: "new", label: "New", color: "text-blue-400", bg: "bg-blue-500/10 border-blue-500/20" },
    { value: "Interested", label: "Interested", color: "text-emerald-400", bg: "bg-emerald-500/10 border-emerald-500/20" },
    { value: "Not Interested", label: "Not Interested", color: "text-rose-400", bg: "bg-rose-500/10 border-rose-500/20" },
    { value: "Pending", label: "Pending", color: "text-amber-400", bg: "bg-amber-500/10 border-amber-500/20" },
    { value: "contacted", label: "Contacted", color: "text-cyan-400", bg: "bg-cyan-500/10 border-cyan-500/20" },
    { value: "converted", label: "Converted ✓", color: "text-green-400", bg: "bg-green-500/10 border-green-500/20" },
    { value: "lost", label: "Lost", color: "text-gray-400", bg: "bg-gray-500/10 border-gray-500/20" },
];

const INTERACTION_TYPES = [
    { value: "Call", label: "Phone Call", icon: Phone },
    { value: "Meeting", label: "Meeting", icon: User },
    { value: "Message", label: "Message/Chat", icon: MessageSquare },
    { value: "Site Visit", label: "Site Visit", icon: Target },
    { value: "Other", label: "Other", icon: Activity },
];

const getStatusInfo = (status) => {
    const found = LEAD_STATUSES.find(s => s.value === status);
    return found || { label: status, color: "text-gray-400", bg: "bg-gray-500/10 border-gray-500/20" };
};

const LeadFollowUpModal = ({ isOpen, onClose, lead, onUpdate }) => {
    const { theme } = useSidebarContext();
    const { enqueueSnackbar } = useSnackbar();
    const authState = useSelector((state) => state.auth);
    const { user, activeUser } = authState;
    const currentUser = user || activeUser;

    const [followUps, setFollowUps] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [remarks, setRemarks] = useState("");
    const [nextDate, setNextDate] = useState("");
    const [leadStatus, setLeadStatus] = useState("");
    const [interactionType, setInteractionType] = useState("Call");

    const fetchHistory = async () => {
        if (!lead?._id) return;
        setLoading(true);
        try {
            const res = await getLeadFollowUps(lead._id);
            if (res.success) {
                setFollowUps(res.data);
            }
        } catch (error) {
            console.error("Error fetching lead history:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen && lead?._id) {
            fetchHistory();
            setRemarks("");
            setNextDate("");
            setLeadStatus("");
            setInteractionType("Call");
        }
    }, [isOpen, lead?._id]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!remarks.trim()) return;

        const followUpData = {
            lead_id: lead._id,
            remarks: `[${interactionType}] ${remarks}`,
            next_followup_date: nextDate || null,
            leadStatus: leadStatus || undefined,
        };

        try {
            setSubmitting(true);
            const res = await createLeadFollowUp(followUpData);
            if (res.success) {
                enqueueSnackbar("Follow-up logged successfully", { variant: "success" });
                setRemarks("");
                setNextDate("");
                setLeadStatus("");
                setInteractionType("Call");
                fetchHistory();
                if (onUpdate) onUpdate(leadStatus || null);
            }
        } catch (error) {
            enqueueSnackbar("Failed to log follow-up", { variant: "error" });
        } finally {
            setSubmitting(false);
        }
    };

    if (!isOpen || !lead) return null;

    const typeIcon = INTERACTION_TYPES.find(t => t.value === interactionType);
    const TypeIcon = typeIcon?.icon || Phone;

    return (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/80 backdrop-blur-sm p-4">
            <div
                className={`${theme.tailwind.bg19} w-full max-w-5xl h-[88vh] rounded-2xl shadow-2xl border ${theme.tailwind.border17} flex overflow-hidden`}
            >
                {/* LEFT: Timeline History */}
                <div className={`flex-1 flex flex-col border-r ${theme.tailwind.border17}`}>
                    {/* Header */}
                    <div className={`p-5 border-b ${theme.tailwind.border17} flex items-center justify-between bg-black/10`}>
                        <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-xl bg-blue-500/10 border border-blue-500/20`}>
                                <Clock className="w-5 h-5 text-blue-400" />
                            </div>
                            <div>
                                <h2 className={`text-lg font-bold ${theme.tailwind.text9}`}>
                                    Follow-up Timeline
                                </h2>
                                <p className={`text-xs ${theme.tailwind.text12} opacity-60`}>
                                    {lead.name} &mdash; {followUps.length} interaction{followUps.length !== 1 ? "s" : ""}
                                </p>
                            </div>
                        </div>
                        {/* Lead current status pill */}
                        <div className={`px-3 py-1 rounded-full text-xs font-bold border ${getStatusInfo(lead.status).bg} ${getStatusInfo(lead.status).color}`}>
                            {getStatusInfo(lead.status).label}
                        </div>
                    </div>

                    {/* Timeline list */}
                    <div className="flex-1 overflow-y-auto p-6 space-y-0">
                        {loading && followUps.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-full gap-3 opacity-60">
                                <div className="w-8 h-8 border-4 border-blue-500/40 border-t-blue-500 rounded-full animate-spin" />
                                <p className="text-sm">Loading history...</p>
                            </div>
                        ) : followUps.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-full gap-4 opacity-40 text-center">
                                <Clock className="w-16 h-16 opacity-20" />
                                <div>
                                    <p className="font-medium mb-1">No follow-ups yet</p>
                                    <p className="text-sm">Log your first interaction to start tracking</p>
                                </div>
                            </div>
                        ) : (
                            followUps.map((item, index) => {
                                // Parse interaction type from remarks prefix
                                const typeMatch = item.remarks?.match(/^\[([^\]]+)\]\s*/);
                                const parsedType = typeMatch ? typeMatch[1] : null;
                                const cleanRemarks = parsedType ? item.remarks.replace(/^\[[^\]]+\]\s*/, "") : item.remarks;
                                const itemTypeInfo = INTERACTION_TYPES.find(t => t.value === parsedType);
                                const ItemIcon = itemTypeInfo?.icon || Clock;
                                const statusInfo = item.leadStatusAtTime ? getStatusInfo(item.leadStatusAtTime) : null;

                                return (
                                    <div key={item._id} className="relative pl-10 pb-8 last:pb-0 group">
                                        {/* Connector line */}
                                        {index !== followUps.length - 1 && (
                                            <div className={`absolute left-[19px] top-10 bottom-0 w-[2px] ${theme.tailwind.bg17} group-hover:bg-blue-500/20 transition-colors`} />
                                        )}

                                        {/* Icon dot */}
                                        <div className={`absolute left-0 top-1 w-10 h-10 rounded-full ${theme.tailwind.bg3} border-2 ${theme.tailwind.border17} flex items-center justify-center z-10 group-hover:border-blue-500/50 transition-all group-hover:scale-110`}>
                                            <ItemIcon className="w-4 h-4 text-blue-400" />
                                        </div>

                                        {/* Card */}
                                        <div className={`rounded-xl border ${theme.tailwind.border17} ${theme.tailwind.bg2} transition-all duration-200 shadow-sm p-4 group-hover:shadow-md group-hover:border-blue-500/20`}>
                                            {/* Card header */}
                                            <div className="flex justify-between items-start mb-3">
                                                <div className="flex items-center gap-2">
                                                    <div className={`w-8 h-8 rounded-full bg-gradient-to-br from-blue-500/20 to-indigo-500/10 border border-blue-500/20 flex items-center justify-center flex-shrink-0`}>
                                                        <User className="w-3.5 h-3.5 text-blue-400" />
                                                    </div>
                                                    <div>
                                                        <span className={`block text-sm font-semibold ${theme.tailwind.text10} leading-tight`}>
                                                            {item.created_by?.employeeData?.name || item.created_by?.username || "System"}
                                                        </span>
                                                        <span className={`text-[10px] uppercase tracking-wider ${theme.tailwind.text12} opacity-60`}>
                                                            {item.created_by?.role || "Employee"}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="flex flex-col items-end gap-2">
                                                    <span className={`text-xs ${theme.tailwind.text12} opacity-70`}>
                                                        {new Date(item.date).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })}
                                                    </span>
                                                    {/* Interaction type tag */}
                                                    {parsedType && (
                                                        <span className="text-[10px] font-bold uppercase tracking-wider text-blue-400 bg-blue-500/10 px-2 py-0.5 rounded-full border border-blue-500/20">
                                                            {parsedType}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Remarks */}
                                            <p className={`${theme.tailwind.text9} text-sm leading-relaxed mb-3 whitespace-pre-wrap`}>
                                                {cleanRemarks}
                                            </p>

                                            {/* Footer tags */}
                                            <div className="flex flex-wrap gap-2">
                                                {statusInfo && (
                                                    <div className={`inline-flex items-center gap-1.5 text-xs font-semibold border px-2.5 py-1 rounded-full ${statusInfo.bg} ${statusInfo.color}`}>
                                                        <CheckCircle className="w-3 h-3" />
                                                        Status → {statusInfo.label}
                                                    </div>
                                                )}
                                                {item.next_followup_date && (
                                                    <div className={`inline-flex items-center gap-1.5 text-xs font-medium text-amber-400 bg-amber-500/10 border border-amber-500/20 px-2.5 py-1 rounded-full`}>
                                                        <Calendar className="w-3 h-3" />
                                                        Next: {new Date(item.next_followup_date).toLocaleDateString()}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>

                {/* RIGHT: Add Follow-up Form */}
                <div className={`w-[360px] flex flex-col ${theme.tailwind.bg2}`}>
                    <div className={`p-5 border-b ${theme.tailwind.border17} flex justify-between items-center bg-black/10`}>
                        <div>
                            <h3 className={`font-semibold ${theme.tailwind.text10} text-base`}>Log Follow-up</h3>
                            <p className={`text-xs ${theme.tailwind.text12} opacity-60 mt-0.5`}>Record your interaction with this lead</p>
                        </div>
                        <button
                            onClick={onClose}
                            className={`p-2 rounded-full hover:bg-red-500/10 hover:text-red-400 ${theme.tailwind.text5} transition-all`}
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-5 space-y-4">
                        {/* Interaction Type */}
                        <div>
                            <label className={`block text-xs font-semibold uppercase tracking-wider ${theme.tailwind.text12} mb-2`}>
                                Interaction Type
                            </label>
                            <div className="grid grid-cols-3 gap-1.5">
                                {INTERACTION_TYPES.map((t) => {
                                    const TIcon = t.icon;
                                    return (
                                        <button
                                            key={t.value}
                                            type="button"
                                            onClick={() => setInteractionType(t.value)}
                                            className={`flex flex-col items-center gap-1 p-2.5 rounded-xl border transition-all text-xs font-medium
                        ${interactionType === t.value
                                                    ? "bg-blue-500/20 border-blue-500/40 text-blue-400"
                                                    : `${theme.tailwind.bg3} ${theme.tailwind.border17} ${theme.tailwind.text12} hover:border-blue-500/20`
                                                }`}
                                        >
                                            <TIcon className="w-4 h-4" />
                                            <span className="text-[10px] leading-tight text-center">{t.label}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Remarks */}
                        <div>
                            <label className={`block text-xs font-semibold uppercase tracking-wider ${theme.tailwind.text12} mb-2`}>
                                Interaction Notes *
                            </label>
                            <textarea
                                value={remarks}
                                onChange={(e) => setRemarks(e.target.value)}
                                placeholder="What was discussed? What was the outcome?..."
                                className={`w-full min-h-[140px] px-4 py-3 rounded-xl ${theme.tailwind.bg3} border ${theme.tailwind.border17} focus:border-blue-500/40 focus:ring-1 focus:ring-blue-500/20 outline-none ${theme.tailwind.text9} transition-all resize-none placeholder-gray-500/50 text-sm leading-relaxed`}
                                autoFocus
                                required
                            />
                        </div>

                        {/* Update Lead Status */}
                        <div>
                            <label className={`block text-xs font-semibold uppercase tracking-wider ${theme.tailwind.text12} mb-2`}>
                                Update Lead Status <span className="opacity-50 normal-case font-normal">(optional)</span>
                            </label>
                            <select
                                value={leadStatus}
                                onChange={(e) => setLeadStatus(e.target.value)}
                                className={`w-full px-4 py-3 rounded-xl ${theme.tailwind.bg3} border ${theme.tailwind.border17} focus:border-blue-500/40 outline-none ${theme.tailwind.text9} transition-all text-sm`}
                            >
                                {LEAD_STATUSES.map(s => (
                                    <option key={s.value} value={s.value}>{s.label}</option>
                                ))}
                            </select>
                            {leadStatus && (
                                <div className={`mt-2 flex items-center gap-2 text-xs font-semibold border px-3 py-1.5 rounded-lg ${getStatusInfo(leadStatus).bg} ${getStatusInfo(leadStatus).color}`}>
                                    <CheckCircle className="w-3.5 h-3.5" />
                                    Lead status will be updated to: {getStatusInfo(leadStatus).label}
                                </div>
                            )}
                        </div>

                        {/* Next Follow-up Date */}
                        <div>
                            <label className={`block text-xs font-semibold uppercase tracking-wider ${theme.tailwind.text12} mb-2`}>
                                Next Follow-up Date <span className="opacity-50 normal-case font-normal">(optional)</span>
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Calendar className={`h-4 w-4 ${theme.tailwind.text14} opacity-60`} />
                                </div>
                                <input
                                    type="date"
                                    value={nextDate}
                                    onChange={(e) => setNextDate(e.target.value)}
                                    className={`w-full pl-10 pr-4 py-3 rounded-xl ${theme.tailwind.bg3} border ${theme.tailwind.border17} focus:border-blue-500/40 outline-none ${theme.tailwind.text9} transition-all text-sm`}
                                    min={new Date().toISOString().split("T")[0]}
                                />
                            </div>
                        </div>

                        {/* Submit */}
                        <button
                            type="submit"
                            disabled={submitting || !remarks.trim()}
                            className={`w-full py-3.5 rounded-xl font-bold text-sm tracking-wide transition-all uppercase flex items-center justify-center gap-2 shadow-lg
                ${submitting || !remarks.trim()
                                    ? "opacity-40 cursor-not-allowed bg-blue-500/20 text-blue-400 border border-blue-500/20"
                                    : "bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white shadow-blue-500/20 hover:shadow-blue-500/30 hover:shadow-xl transform hover:-translate-y-0.5"
                                }`}
                        >
                            {submitting ? (
                                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            ) : (
                                <Plus className="w-5 h-5" />
                            )}
                            {submitting ? "Saving..." : "Log Interaction"}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default LeadFollowUpModal;
